import React from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { PermissionAction, permissionsService, PermissionType } from '../../services/permissions.service'
import './PermissionsSelect.scss'

interface PermissionFormData {
  [type: string]: {
    [action: string]: boolean
  }
}

interface PermissionsSelectProps {
  title: string
  value: string[]
  onChange: (permissions: string[]) => void
  error?: string
  inheritedPermissions?: string[]
  disabled?: boolean
}

export const PermissionsSelect: React.FC<PermissionsSelectProps> = ({
  title,
  value,
  onChange,
  error,
  inheritedPermissions = [],
  disabled = false,
}) => {
  const { t } = useTranslation()

  // Memoize the permission types and actions to prevent infinite loops
  const allowedTypes = React.useMemo(() => permissionsService.getAllPermissionTypes(), [])
  const permissionActions = React.useMemo(() => permissionsService.getAllPermissionActions(), [])
  const actionsByType = React.useMemo(() => {
    const map: Record<string, string[]> = {}
    allowedTypes.forEach((type) => {
      map[type] = permissionsService.getPermissionActionsForType(type)
    })
    return map
  }, [allowedTypes])

  // Calculate form data directly without state
  const formData = React.useMemo(() => {
    const newFormData: PermissionFormData = {}

    // Initialize form data structure
    allowedTypes.forEach((type) => {
      newFormData[type] = {}
      actionsByType[type].forEach((action) => {
        newFormData[type][action] = false
      })
    })

    // Apply permissions from value and inherited permissions
    const allPerms = [...inheritedPermissions, ...value]
    allPerms.forEach((permissionStr) => {
      const { actionKey, typeKey } = permissionsService.getPermissionKeys(permissionStr)
      if (actionKey && typeKey && newFormData[typeKey] && actionKey in newFormData[typeKey]) {
        newFormData[typeKey][actionKey] = true
      }
    })

    return newFormData
  }, [value, inheritedPermissions, allowedTypes, actionsByType])

  // Calculate types with all actions
  const typesWithAllActions = React.useMemo(() => {
    const newTypesWithAllActions = new Set<string>()

    allowedTypes.forEach((type) => {
      if (formData[type]) {
        const typeActions = actionsByType[type]
        const allActionsSelected = typeActions.every((action) => formData[type][action])
        if (allActionsSelected && typeActions.length > 0) {
          newTypesWithAllActions.add(type)
        }
      }
    })

    return newTypesWithAllActions
  }, [formData, allowedTypes, actionsByType])

  const handleActionChange = (typeKey: string, actionKey: string, checked: boolean) => {
    // Create new permissions array with the changed permission
    const newPermissions = [...value]
    const permissionCode = permissionsService.getPermissionCode(
      PermissionAction[actionKey as keyof typeof PermissionAction],
      PermissionType[typeKey as keyof typeof PermissionType]
    )

    if (checked) {
      // Add permission if not already present
      if (!newPermissions.includes(permissionCode)) {
        newPermissions.push(permissionCode)
      }
    } else {
      // Remove permission
      const index = newPermissions.indexOf(permissionCode)
      if (index > -1) {
        newPermissions.splice(index, 1)
      }
    }

    onChange(newPermissions)
  }

  const handleToggleAll = (typeKey: string, checked: boolean) => {
    // Create new permissions array
    const newPermissions = [...value]
    const typeActions = actionsByType[typeKey]

    // Remove all existing permissions for this type
    const existingPermissions = newPermissions.filter((perm) => {
      const { typeKey: permTypeKey, actionKey } = permissionsService.getPermissionKeys(perm)
      if (permTypeKey !== typeKey) return true
      return !actionKey || !typeActions.includes(actionKey)
    })

    if (checked) {
      typeActions.forEach((action) => {
        const permissionCode = permissionsService.getPermissionCode(
          PermissionAction[action as keyof typeof PermissionAction],
          PermissionType[typeKey as keyof typeof PermissionType]
        )
        if (!existingPermissions.includes(permissionCode)) {
          existingPermissions.push(permissionCode)
        }
      })
    }

    onChange(existingPermissions)
  }

  const isInherited = (typeKey: string, actionKey: string): boolean => {
    const permissionCode = permissionsService.getPermissionCode(
      PermissionAction[actionKey as keyof typeof PermissionAction],
      PermissionType[typeKey as keyof typeof PermissionType]
    )
    return inheritedPermissions.includes(permissionCode)
  }

  return (
    <div className="permissions-select">
      <Form.Label className="fw-bold">{title}</Form.Label>
      {error && <div className="text-danger small mb-2">{error}</div>}

      <div className="table-responsive">
        <ul className="list-group">
          {/* Header row */}
          <li className="list-group-item d-flex">
            <div className="col-3">{t('type')}</div>
            <div className="col">{t('all')}</div>
            {permissionActions.map((action) => (
              <div className="col" key={action}>
                {t(action.toLowerCase())}
              </div>
            ))}
          </li>

          {/* Permission rows */}
          {allowedTypes.map((type) => {
            const typeActions = actionsByType[type]
            return (
              <li key={type} className="list-group-item d-flex">
                <div className="col-3">{t(type.replace('%s_', '').toLowerCase())}:</div>
                <div className="col form-check form-check-inline form-switch">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={`${type}_all`}
                    checked={typesWithAllActions.has(type)}
                    onChange={(e) => handleToggleAll(type, e.target.checked)}
                    disabled={disabled}
                  />
                  <Form.Label htmlFor={`${type}_all`} className="form-check-label visually-hidden">
                    {t('all')}
                  </Form.Label>
                </div>
                {permissionActions.map((action) => {
                  if (!typeActions.includes(action)) {
                    return <div className="col" key={action} />
                  }
                  const isInheritedPermission = isInherited(type, action)
                  return (
                    <div className="col form-check form-check-inline" key={action}>
                      <input
                        type="checkbox"
                        className={`form-check-input ${isInheritedPermission ? 'text-muted' : ''}`}
                        id={`${type}_${action}`}
                        checked={formData[type]?.[action] || false}
                        onChange={(e) => handleActionChange(type, action, e.target.checked)}
                        disabled={disabled || isInheritedPermission}
                      />
                      <Form.Label htmlFor={`${type}_${action}`} className="form-check-label visually-hidden">
                        {action}
                      </Form.Label>
                    </div>
                  )
                })}
              </li>
            )
          })}
        </ul>
      </div>
    </div>
  )
}

export default PermissionsSelect
